import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { SidebarProvider } from './context/SidebarContext';
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TaskList from './pages/TaskList';

// Lazy load TaskDetail component for code splitting
const TaskDetail = lazy(() => import('./pages/TaskDetail'));
const Subscription = lazy(() => import('./pages/Subscription'));

// Component to handle legacy /tasks/:id redirect
const LegacyTaskDetailRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/app/tasks/${id}`} replace />;
};

function AppContent() {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';
  const isAuthPage = ['/login', '/register', '/forgot-password', '/reset-password'].includes(location.pathname);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!isLandingPage && <Navbar isMinimal={isAuthPage} />}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: isLandingPage ? 0 : 3,
        }}
      >
        <Routes>
          {/* Public Landing Page */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            }
          />
          
          {/* Public Auth Pages */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />
          
          {/* App Routes - Redirect /app to /app/tasks */}
          <Route
            path="/app"
            element={<Navigate to="/app/tasks" replace />}
          />
          
          {/* Protected App Routes */}
          <Route
            path="/app/tasks"
            element={
              <PrivateRoute>
                <TaskList />
              </PrivateRoute>
            }
          />
          <Route
            path="/app/tasks/:id"
            element={
              <PrivateRoute>
                <Suspense fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                  </Box>
                }>
                  <TaskDetail />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/app/subscription"
            element={
              <PrivateRoute>
                <Suspense fallback={
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress />
                  </Box>
                }>
                  <Subscription />
                </Suspense>
              </PrivateRoute>
            }
          />
          
          {/* Legacy routes - redirect to new app routes */}
          <Route
            path="/tasks"
            element={<Navigate to="/app/tasks" replace />}
          />
          <Route
            path="/tasks/:id"
            element={
              <LegacyTaskDetailRedirect />
            }
          />
          
          {/* Catch all - redirect to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
        <SidebarProvider>
          <AppContent />
        </SidebarProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App; 